/**
 * Deposit React Query Hooks
 * Story 46-1: Fizetési mód választó - MyPOS integráció
 * Handles deposit operations (SALE/REFUND) via MyPOS
 */

import { api } from '@/api/client';
import type { LoyaltyCommitPayload } from '@/api/loyalty-redeem';
// Q-T181 — a CAS-ütközés felismerése (ADR-137 #310 wire-contract), a két hibaág szétválasztásához
import { isInventoryCasConflict } from '@/api/rentals';
import { useTranslation } from '@kgc/i18n';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PaymentMethod } from '../types';

// ============================================
// Types
// ============================================

export interface DepositSaleRequest {
  rentalId: string;
  amount: number;
  currency: 'HUF';
  paymentMethod: 'CARD' | 'CASH';
}

export interface DepositSaleResponse {
  transactionId: string;
  status: 'SUCCESS' | 'PENDING' | 'FAILED';
  myposReference?: string;
  message?: string;
}

export interface DepositRefundRequest {
  depositId: string;
  amount: number;
  reason?: string;
}

export interface DepositRefundResponse {
  transactionId: string;
  status: 'SUCCESS' | 'PENDING' | 'FAILED';
  refundedAmount: number;
  myposReference?: string;
}

/**
 * Cross-sell additional product item (Story Q-XSL-1)
 */
export interface AdditionalItemRequest {
  productId: string;
  quantity: number;
  unitPrice: number;
}

/**
 * E-105 Phase 2 / ADR-108: a single package-selection in the rental create
 * payload. The backend service expands `packageId` into a snapshot, persists
 * the selection row, and links resulting consignment items via the
 * `clientSelectionId` idempotency key.
 */
export interface PackageSelectionPayload {
  clientSelectionId: string;
  equipmentId: string;
  packageId: string;
  qtyOverrides?: Record<string, number>;
}

export interface CreateRentalRequest {
  partnerId: string;
  /** Cost bearer partner ID — költségviselő, aki fizet (ha más mint a bérlő) */
  costBearerId?: string | undefined;
  items: Array<{
    equipmentId: string;
    quantity: number;
  }>;
  startDate: string;
  endDate: string;
  startTime?: string; // HH:MM format
  endTime?: string; // HH:MM format
  useHourlyBilling?: boolean;
  notes?: string;
  /**
   * UPPERCASE `PaymentMethod` enum (NEM lowercase string). A createRental mappel
   * lowercase backend-formátumra egy helyen (:155, SSoT). A szigorú enum-típus a
   * recurrence-őr: a tsc elkapja, ha valaki pre-mappelt lowercase-t adna át
   * (CARD/MyPOS case-mismatch verify-fogás 2026-06-02).
   */
  paymentMethod: PaymentMethod;
  depositAmount: number;
  /** Deposit paid at checkout (kaució befizetve a helyszínen) */
  depositPaid?: boolean;
  /** Cross-sell products purchased alongside the rental (Story Q-XSL-1) */
  additionalItems?: AdditionalItemRequest[];
  /** Story 70-1E-a: operator override for expired partner ID card. */
  overrideExpiredId?: boolean;
  /** Story 70-1E-a: mandatory reason when overrideExpiredId=true (FR70 audit). */
  overrideReason?: string;
  /**
   * E-105 Phase 2 / ADR-108: 1:N package selections per equipment. Backend
   * gates this with the `BERLES_MULTI_PACKAGE` per-tenant feature flag and
   * returns HTTP 403 if the flag is OFF.
   */
  packageSelections?: PackageSelectionPayload[];
  /**
   * FE-E (ADR-103 Am-5 D-5.2): hűségpont-beváltás commit-spend. A backend a
   * friss rentalId-re kulcsolja a draftId-idempotency-kulccsal (dupla-submit
   * ugyanazzal a draftId-vel → a pont CSAK EGYSZER vonódik).
   */
  loyaltyRedeem?: LoyaltyCommitPayload;
}

export interface CreateRentalResponse {
  id: string;
  contractNumber: string;
  status: 'ACTIVE' | 'DRAFT';
}

// ============================================
// Query Keys
// ============================================

export const depositKeys = {
  all: ['deposits'] as const,
  byRental: (rentalId: string) => [...depositKeys.all, 'rental', rentalId] as const,
};

export const rentalKeys = {
  all: ['rentals'] as const,
  detail: (id: string) => [...rentalKeys.all, id] as const,
};

// ============================================
// API Functions
// ============================================

async function processDepositSale(dto: DepositSaleRequest): Promise<DepositSaleResponse> {
  const res = await api.post<{ data: DepositSaleResponse }>('/deposits/sale', dto);
  if (!res.data) {
    throw new Error('Invalid API response: missing deposit sale data');
  }
  return res.data;
}

async function processDepositRefund(dto: DepositRefundRequest): Promise<DepositRefundResponse> {
  const res = await api.post<{ data: DepositRefundResponse }>('/deposits/refund', dto);
  if (!res.data) {
    throw new Error('Invalid API response: missing deposit refund data');
  }
  return res.data;
}

async function createRental(dto: CreateRentalRequest): Promise<CreateRentalResponse> {
  // Map frontend field names and enum values to backend DTO
  // Backend DepositPaymentMethod enum uses lowercase: 'cash', 'card', 'bank_transfer'
  const paymentMethodMapping: Record<string, string> = {
    CASH: 'cash',
    CARD: 'card',
    TRANSFER: 'bank_transfer',
  };

  // Build backend DTO — includes cross-sell additionalItems if present (Q-XSL-1)
  const backendDto: Record<string, unknown> = {
    partnerId: dto.partnerId,
    ...(dto.costBearerId ? { costBearerId: dto.costBearerId } : {}),
    items: dto.items.map(item => ({
      inventoryItemId: item.equipmentId,
      quantity: item.quantity,
    })),
    startDate: dto.startDate,
    plannedEndDate: dto.endDate,
    depositPaymentMethod: paymentMethodMapping[dto.paymentMethod] ?? 'cash',
    notes: dto.notes,
    ...(dto.startTime ? { startTime: dto.startTime } : {}),
    ...(dto.endTime ? { endTime: dto.endTime } : {}),
    ...(dto.useHourlyBilling ? { useHourlyBilling: dto.useHourlyBilling } : {}),
    ...(dto.depositPaid ? { depositPaid: true } : {}),
    // Story 70-1E-a: override expired ID card (FR70 audit fires server-side).
    ...(dto.overrideExpiredId ? { overrideExpiredId: true } : {}),
    ...(dto.overrideExpiredId && dto.overrideReason ? { overrideReason: dto.overrideReason } : {}),
    // FE-E: loyalty commit-spend a friss rentalId-re (draftId idempotency)
    ...(dto.loyaltyRedeem ? { loyaltyRedeem: dto.loyaltyRedeem } : {}),
  };

  // Append cross-sell products if any (Story Q-XSL-1)
  if (dto.additionalItems && dto.additionalItems.length > 0) {
    backendDto.additionalItems = dto.additionalItems;
  }
  // E-105 Phase 2 / ADR-108: forward 1:N package selections when present.
  // Backend validates each clientSelectionId UUID, snapshots the package, and
  // returns HTTP 403 if BERLES_MULTI_PACKAGE feature flag is OFF for the tenant.
  if (dto.packageSelections && dto.packageSelections.length > 0) {
    backendDto.packageSelections = dto.packageSelections;
  }
  const res = await api.post<Record<string, unknown>>('/rentals', backendDto);
  // Backend returns { data: { id, rentalNumber, status, ... }, depositPaymentMethod }
  const rental = (res as { data?: Record<string, unknown> }).data ?? res;
  return {
    id: rental.id as string,
    contractNumber: (rental.rentalNumber ?? rental.contractNumber ?? '') as string,
    status: (rental.status ?? 'DRAFT') as 'ACTIVE' | 'DRAFT',
  };
}

// ============================================
// React Query Hooks
// ============================================

/**
 * Hook to process deposit sale (collect deposit via MyPOS SALE)
 */
export function useDepositSale() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: processDepositSale,
    onSuccess: (_result, variables) => {
      // Invalidate rental queries to refetch with updated deposit status
      queryClient.invalidateQueries({ queryKey: rentalKeys.detail(variables.rentalId) });
      queryClient.invalidateQueries({ queryKey: depositKeys.byRental(variables.rentalId) });
    },
  });
}

/**
 * Hook to process deposit refund (return deposit via MyPOS REFUND)
 */
export function useDepositRefund() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: processDepositRefund,
    onSuccess: () => {
      // Invalidate all deposits to refetch
      queryClient.invalidateQueries({ queryKey: depositKeys.all });
      queryClient.invalidateQueries({ queryKey: rentalKeys.all });
    },
  });
}

/**
 * Hook to create a new rental
 */
export function useCreateRental() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createRental,
    onSuccess: () => {
      // Invalidate all rentals to refetch the list
      queryClient.invalidateQueries({ queryKey: rentalKeys.all });
    },
  });
}

// ============================================
// Combined Hook for Rental Checkout Flow
// ============================================

export interface CheckoutResult {
  rental: CreateRentalResponse;
  deposit?: DepositSaleResponse;
}

/**
 * Hook for the complete rental checkout flow:
 * 1. Create rental (DRAFT status initially for card payments)
 * 2. Process deposit (if card payment)
 * 3. Activate rental on successful deposit
 *
 * ⚠️ Q-T181 (2026-08-02) — a fenti záró-mondat ELAVULT volt, és a rá épülő hibaszöveg is:
 * a `POST /deposits/sale` a kaució-terhelés UTÁN ACTIVE-ra commitolja a bérlést, és a
 * bérgép-jelölés (`markAsRented`) csak EZUTÁN fut. Ha ott 409 jön, a kaució MÁR LE VAN VONVA
 * és a bérlés MÁR ACTIVE — tehát az újrapróbálás nem „retry", hanem +1 kártyaterhelés,
 * +1 HELD kaució és +1 ACTIVE bérlés. A catch ezért NEM buzdíthat újrapróbálásra.
 */
export function useRentalCheckout() {
  const { t } = useTranslation();
  const createRentalMutation = useCreateRental();
  const depositSaleMutation = useDepositSale();

  const checkout = async (rentalData: CreateRentalRequest): Promise<CheckoutResult> => {
    // Step 1: Create the rental
    // For CARD payments, backend should create as DRAFT initially
    const rental = await createRentalMutation.mutateAsync(rentalData);

    // Step 2: If card payment, process deposit via MyPOS
    if (rentalData.paymentMethod === 'CARD') {
      try {
        const deposit = await depositSaleMutation.mutateAsync({
          rentalId: rental.id,
          amount: rentalData.depositAmount,
          currency: 'HUF',
          paymentMethod: 'CARD',
        });
        // Deposit successful - rental will be activated by backend
        return { rental, deposit };
      } catch (depositError) {
        // Q-T181 — KÉT ellentétes helyzet, ugyanaz a 409. A diszkriminátor a `conflict` mező
        // (BE: deposit-sale.service.ts:1308) — strukturált kód VAN → a gép máshoz van jelölve;
        // kód NINCS → a gép sehova nincs jelölve, akár a pultnál is állhat.
        // Egy közös mondat a (2)-esnél „foglalt a gép"-et hazudna a pultosnak.
        if (isInventoryCasConflict(depositError)) {
          throw new Error(
            t('error.rental.depositPaidEquipmentTaken', {
              contractNumber: rental.contractNumber,
            })
          );
        }
        // ⏳ ÁTMENETI ÁG — a Q-T181 B) (jelölés + státusz-write egy tranzakcióban) landolásakor
        // nem keletkezhet többé „ACTIVE, de jelöletlen" állapot ⇒ ez a kulcs kivezetendő,
        // különben árván marad egy soha nem tüzelő üzenet 4 nyelven.
        throw new Error(
          t('error.rental.depositPaidNotRecorded', {
            contractNumber: rental.contractNumber,
          })
        );
      }
    }

    // Cash or transfer - confirm pickup to activate rental
    try {
      const { confirmPickup } = await import('@/api/rentals');
      await confirmPickup(rental.id, {
        accessoryChecklistVerified: true,
        depositCollected: true,
        depositMethod: rentalData.paymentMethod === 'CASH' ? 'cash' : undefined,
      });
    } catch (pickupError) {
      // Q-T138: a pickup-hibát NEM némítjuk. Eddig üres catch → a bérlés Vázlatban ragadt,
      // de a wizard „sikeresen" az adatlapra navigált (a hiba nyomtalanul eltűnt). Throw →
      // a RentalWizardPage catch-e destruktív toastot mutat + a user a wizardon marad (retry).
      // Szimmetrikus a CARD-deposit-ággal (fentebb).
      throw new Error(
        t('error.rental.pickupFailed', {
          error:
            pickupError instanceof Error ? pickupError.message : t('error.common.unknownError'),
        })
      );
    }
    return { rental };
  };

  return {
    checkout,
    isLoading: createRentalMutation.isPending || depositSaleMutation.isPending,
    error: createRentalMutation.error || depositSaleMutation.error,
    isSuccess: createRentalMutation.isSuccess,
  };
}
